import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { AlertTriangle, CheckCircle2, RefreshCw, ArrowRight, Loader2, UserPlus, ChevronsUpDown, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AgentCombobox = ({ agents, value, onSelect, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = agents.find((a) => a.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-64 justify-between font-normal" data-testid={testId}>
          <span className="truncate">
            {selected ? `${selected.full_name || selected.id}${selected.primary_marca_code ? ` (${selected.primary_marca_code})` : ""}` : "— alege agent existent —"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Caută agent..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-56">
            <CommandEmpty>Niciun agent găsit</CommandEmpty>
            {agents.map((a) => (
              <CommandItem
                key={a.id}
                value={`${a.full_name || ""} ${a.primary_marca_code || ""}`}
                onSelect={() => { onSelect(a.id); setOpen(false); }}
                data-testid={`${testId}-option-${a.id}`}
              >
                <Check className={`mr-2 h-4 w-4 ${value === a.id ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{a.full_name || a.id}{a.primary_marca_code ? ` (${a.primary_marca_code})` : ""}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const Reconciliation = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  const [mappings, setMappings] = useState({}); // key -> agent_id
  const [aqsGroups, setAqsGroups] = useState([]);
  const [aqsMappings, setAqsMappings] = useState({}); // sig -> agent_id

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [unRes, agRes, aqsRes] = await Promise.all([
        axios.get(`${API}/reconciliation/unresolved-agents`),
        axios.get(`${API}/agents?limit=500&active=true`),
        axios.get(`${API}/reconciliation/aqs-unresolved-agents`),
      ]);
      setGroups(unRes.data.data || []);
      setAgents(agRes.data.data || []);
      setAqsGroups(aqsRes.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Faza 1 — panouri noi (dosare blocate + listă de verificat). Rol-gated: eșecul 403 nu rupe pagina.
  const [blockedDossiers, setBlockedDossiers] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewBySituation, setReviewBySituation] = useState({});
  const [reviewSituation, setReviewSituation] = useState("");
  const [withdrawDialog, setWithdrawDialog] = useState(null); // row
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawClose, setWithdrawClose] = useState("terminated");

  const fetchExtra = useCallback(async () => {
    try {
      const rq = await axios.get(`${API}/reconciliation/review-queue?limit=1000${reviewSituation ? `&situation=${reviewSituation}` : ""}`);
      setReviewQueue(rq.data.data || []);
      setReviewBySituation(rq.data.by_situation || {});
    } catch { /* rol insuficient */ }
    try {
      const bd = await axios.get(`${API}/reconciliation/aqs-blocked-dossiers`);
      setBlockedDossiers(bd.data.data || []);
    } catch { /* admin/BO only */ }
  }, [reviewSituation]);

  useEffect(() => { fetchExtra(); }, [fetchExtra]);

  const handleWithdraw = async () => {
    if (!withdrawReason.trim()) { toast.error("Motivul este obligatoriu"); return; }
    try {
      await axios.post(`${API}/reconciliation/mark-sale-withdrawal`, {
        sale_id: withdrawDialog.sale_id, reason: withdrawReason.trim(), close_contract_status: withdrawClose || null,
      });
      toast.success("Vânzare marcată retrasă");
      setWithdrawDialog(null); setWithdrawReason(""); setWithdrawClose("terminated");
      await fetchExtra();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la marcare");
    }
  };

  const SIT_LABELS = {
    "1_renuntare_inainte_incarcare": "Renunțare înainte de încărcare",
    "2_anulat_pod_inactiv": "Anulat / POD inactiv",
    "3_activat_apoi_switch_out": "Activat apoi switch out",
    "4_retur_reprocesabil": "Retur reprocesabil",
    "de_verificat": "De verificat (neclar)",
  };


  const aqsSig = (g) => `${g.source_system}|${g.agent_external_id || ""}|${g.agent_full_name || ""}`;

  const handleResolveAqs = async (g, overrideAgentId) => {
    const agent_id = overrideAgentId || aqsMappings[aqsSig(g)];
    if (!agent_id) { toast.error("Alege un agent existent"); return; }
    try {
      const res = await axios.post(`${API}/reconciliation/resolve-aqs-agent`, {
        agent_id,
        source_system: g.source_system || "easy",
        agent_full_name: g.agent_full_name || null,
        agent_external_id: g.agent_external_id || null,
        agent_marca: g.agent_marca || null,
        save_alias: true,
      });
      toast.success(`Alias ${res.data.alias_saved.join("+") || "—"} salvat · ${res.data.events_marked} dosar(e) reconciliat(e)`);
      setAqsMappings((p) => { const n = { ...p }; delete n[aqsSig(g)]; return n; });
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la asociere");
    }
  };

  const keyOf = (g) => `${g.source_system}|${g.raw_agent_code || ""}|${g.raw_agent_full_name || ""}`;

  const handleMap = (g, agentId) => {
    setMappings((prev) => ({ ...prev, [keyOf(g)]: agentId }));
  };

  // Create agent inline + auto-map
  const [createDialog, setCreateDialog] = useState(null);
  const [createMode, setCreateMode] = useState("sales"); // 'sales' | 'aqs'
  const [newAgentForm, setNewAgentForm] = useState({ full_name: "", primary_marca_code: "" });
  const openCreateForGroup = (g) => {
    setCreateMode("sales");
    setCreateDialog(g);
    setNewAgentForm({
      full_name: g.raw_agent_full_name || `${g.raw_agent_last_name || ""} ${g.raw_agent_first_name || ""}`.trim(),
      primary_marca_code: g.raw_agent_code || "",
    });
  };
  const openCreateForAqs = (g) => {
    setCreateMode("aqs");
    setCreateDialog(g);
    setNewAgentForm({ full_name: g.agent_full_name || "", primary_marca_code: g.agent_marca || "" });
  };
  const handleCreateAgent = async () => {
    if (!newAgentForm.full_name) { toast.error("Numele e obligatoriu"); return; }
    try {
      const res = await axios.post(`${API}/agents`, {
        full_name: newAgentForm.full_name,
        primary_marca_code: newAgentForm.primary_marca_code || null,
        active: true,
      });
      const newAgentId = res.data.id;
      if (createMode === "aqs") {
        const g = createDialog;
        setCreateDialog(null);
        await handleResolveAqs(g, newAgentId);
        return;
      }
      toast.success("Agent creat. Selectat pentru mapping.");
      handleMap(createDialog, newAgentId);
      setCreateDialog(null);
      await fetchData(); // reload agents list
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la creare");
    }
  };

  const handleApply = async () => {
    const entries = Object.entries(mappings).filter(([_, v]) => v);
    if (entries.length === 0) { toast.error("Selectează cel puțin un agent"); return; }
    setSaving(true);
    try {
      const payload = {
        mappings: entries.map(([k, agent_id]) => {
          const [src, code, name] = k.split("|");
          return {
            source_system: src,
            raw_agent_code: code || null,
            raw_agent_full_name: name || null,
            agent_id,
            save_alias: true,
          };
        }),
      };
      const res = await axios.post(`${API}/reconciliation/resolve-agents`, payload);
      toast.success(`${res.data.total_updated} vânzări reasignate + aliasuri salvate`);
      setMappings({});
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la aplicare");
    } finally {
      setSaving(false);
    }
  };

  const total = groups.reduce((s, g) => s + g.count, 0);
  const pendingCount = Object.values(mappings).filter(Boolean).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Reconciliere Vânzări</h1>
          <p className="text-slate-500 mt-1">Mapează agenții bruti din importuri (Easy/CRM/SF) la entități Agent stabile. Aliasurile se salvează permanent.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-2" />Reîncarcă</Button>
          <Button onClick={handleApply} disabled={saving || pendingCount === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Aplică {pendingCount > 0 ? `(${pendingCount})` : ""}
          </Button>
        </div>
      </div>

      <Card className="border-l-4 border-l-amber-500 bg-amber-50/40">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-900" data-testid="total-unassigned">{total}</p>
                <p className="text-sm text-amber-700">Vânzări neasociate ({groups.length} grupuri distincte)</p>
              </div>
            </div>
            <Link to="/sales?reconciliation_status=needs_review"><Button variant="outline" size="sm"><ArrowRight className="h-3 w-3 mr-1" />Vezi listă</Button></Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Grupuri neasociate</CardTitle>
          <CardDescription>
            Fiecare rând = combinație distinctă (sursă + cod brut + nume brut). Selectează un Agent existent sau creează unul nou.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-300" />
              <p className="text-slate-600">Toate vânzările sunt asociate corect</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sursă</TableHead>
                  <TableHead>Cod brut</TableHead>
                  <TableHead>Nume brut</TableHead>
                  <TableHead>Vânzări</TableHead>
                  <TableHead>Atribuie agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => {
                  const k = keyOf(g);
                  return (
                    <TableRow key={k} data-testid={`group-row-${k}`}>
                      <TableCell><Badge variant="outline" className="text-[10px] uppercase">{g.source_system || "manual"}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{g.raw_agent_code || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-sm">{g.raw_agent_full_name || `${g.raw_agent_last_name || ""} ${g.raw_agent_first_name || ""}`.trim() || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell><Badge variant="secondary">{g.count}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <AgentCombobox
                            agents={agents}
                            value={mappings[k] || ""}
                            onSelect={(v) => handleMap(g, v)}
                            testId={`select-agent-${k}`}
                          />
                          <Button variant="outline" size="sm" onClick={() => openCreateForGroup(g)} data-testid={`create-for-${k}`}>
                            <UserPlus className="h-3 w-3 mr-1" />Nou
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* AQS EASY — agenți neasociați (dosare oprite în verificare, fără vânzare creată) */}
      <Card data-testid="aqs-recon-card">
        <CardHeader>
          <CardTitle>Dosare AQS EASY — agenți neasociați</CardTitle>
          <CardDescription>
            Dosare primite din integrarea automată EASY și oprite în „necesită verificare" din cauza agentului neasociat.
            Nu au vânzare creată. Asociază agentul cu unul existent sau creează-l; aliasul easy se salvează permanent.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : aqsGroups.length === 0 ? (
            <div className="text-center py-10">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-emerald-300" />
              <p className="text-slate-600">Niciun dosar AQS blocat pe agent</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sursă</TableHead>
                  <TableHead>Agent EASY</TableHead>
                  <TableHead>Identificator EASY</TableHead>
                  <TableHead>Dosare / evenimente</TableHead>
                  <TableHead>Asociază agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aqsGroups.map((g) => {
                  const sig = aqsSig(g);
                  return (
                    <TableRow key={sig} data-testid={`aqs-group-${sig}`}>
                      <TableCell><Badge variant="outline" className="text-[10px] uppercase">{g.source_system || "easy"}</Badge></TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{g.agent_full_name || <span className="text-slate-400">—</span>}</div>
                        {g.agent_marca && <div className="text-xs text-slate-500">Marca: {g.agent_marca}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{g.agent_external_id || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {(g.events || []).map((ev) => (
                            <div key={ev.event_id} className="text-xs" data-testid={`aqs-event-${ev.event_id}`}>
                              <Badge variant="secondary" className="mr-1">{ev.dossier_uuid?.slice(0, 8)}…</Badge>
                              <span className="text-slate-500">rev {ev.dossier_revision}</span>
                              <div className="text-amber-700 mt-0.5">{ev.reason}</div>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <AgentCombobox
                            agents={agents}
                            value={aqsMappings[sig] || ""}
                            onSelect={(v) => setAqsMappings((p) => ({ ...p, [sig]: v }))}
                            testId={`aqs-select-agent-${sig}`}
                          />
                          <Button variant="outline" size="sm" onClick={() => handleResolveAqs(g)} disabled={!aqsMappings[sig]} data-testid={`aqs-associate-${sig}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />Asociază
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openCreateForAqs(g)} data-testid={`aqs-create-${sig}`}>
                            <UserPlus className="h-3 w-3 mr-1" />Nou
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Faza 1 — Dosare AQS blocate cu POD + identificatori (admin/BO) */}
      <Card data-testid="aqs-blocked-card">
        <CardHeader>
          <CardTitle>Dosare AQS blocate — POD și identificatori</CardTitle>
          <CardDescription>
            Dosare AQS oprite în „necesită verificare", cu POD-ul și identificatorii minimi pentru decizie (fără date personale complete).
            Se populează pentru dosarele primite de la activare încolo. Vizibil doar admin/back-office.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {blockedDossiers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Niciun dosar blocat cu POD înregistrat</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>POD</TableHead>
                  <TableHead>Nr. contract nou</TableHead>
                  <TableHead>Client (ref)</TableHead>
                  <TableHead>Agent brut</TableHead>
                  <TableHead>Motiv</TableHead>
                  <TableHead>Vechime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedDossiers.map((b, i) => (
                  <TableRow key={`${b.event_id}-${i}`} data-testid={`blocked-dossier-${i}`}>
                    <TableCell className="font-mono text-xs">{b.pod || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm">{b.new_contract_number || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{b.client_ref || "—"}</TableCell>
                    <TableCell className="text-sm">{b.raw_agent_full_name || "—"}{b.raw_agent_marca ? ` (${b.raw_agent_marca})` : ""}</TableCell>
                    <TableCell className="text-xs text-amber-700 max-w-sm">{b.reason}</TableCell>
                    <TableCell className="text-xs">{b.age_days != null ? `${b.age_days}z` : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Faza 1 — Listă de renunțări/returnări de verificat (clasificată, read-only) */}
      <Card data-testid="review-queue-card">
        <CardHeader>
          <CardTitle>Renunțări / returnări de verificat</CardTitle>
          <CardDescription>
            Contracte încă pozitive unde sursa deja importată (Salesforce / CRM legacy) semnalează închidere. Clasificate pe situație;
            cazurile neclare → „de verificat". Lista NU schimbă nimic automat — marcarea e manuală.
          </CardDescription>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant={reviewSituation === "" ? "default" : "outline"} size="sm" onClick={() => setReviewSituation("")} data-testid="review-filter-all">Toate ({reviewQueue.length})</Button>
            {Object.keys(SIT_LABELS).map((k) => (
              <Button key={k} variant={reviewSituation === k ? "default" : "outline"} size="sm" onClick={() => setReviewSituation(k)} data-testid={`review-filter-${k}`}>
                {SIT_LABELS[k]}{reviewBySituation[k] ? ` (${reviewBySituation[k]})` : ""}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {reviewQueue.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Niciun caz de verificat</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contract</TableHead>
                  <TableHead>POD</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Situație</TableHead>
                  <TableHead>CRM status</TableHead>
                  <TableHead>Semnal sursă</TableHead>
                  <TableHead>Acțiune</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewQueue.slice(0, 300).map((r, i) => (
                  <TableRow key={`${r.contract_id}-${i}`} data-testid={`review-row-${i}`}>
                    <TableCell className="text-sm">{r.contract_number || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="font-mono text-xs">{r.pod || "—"}</TableCell>
                    <TableCell className="text-sm">{r.client || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{SIT_LABELS[r.situation] || r.situation}</Badge></TableCell>
                    <TableCell className="text-xs"><Badge variant="secondary">{r.crm_status}</Badge> <span className="text-slate-400">{r.crm_status_date}</span></TableCell>
                    <TableCell className="text-xs text-amber-700">{r.source_signal} <span className="text-slate-400">{r.source_signal_date}</span></TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" disabled={!r.sale_id} onClick={() => { setWithdrawDialog(r); setWithdrawReason(""); setWithdrawClose("terminated"); }} data-testid={`review-mark-${i}`}>
                        <AlertTriangle className="h-3 w-3 mr-1" />Marchează retras
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog marcare retras (manual BO) */}
      <Dialog open={!!withdrawDialog} onOpenChange={() => setWithdrawDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marchează vânzarea ca retrasă</DialogTitle>
            <DialogDescription>
              Contract {withdrawDialog?.contract_number} · POD {withdrawDialog?.pod}. Se păstrează istoricul; se lasă urmă de audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motiv *</Label>
              <Input value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} placeholder="ex. renunțare client înainte de Salesforce" data-testid="withdraw-reason" />
            </div>
            <div>
              <Label>Status contract după marcare</Label>
              <div className="flex gap-2 mt-1">
                {["terminated", "cancelled", "withdrawn", "rejected", ""].map((s) => (
                  <Button key={s || "none"} type="button" size="sm" variant={withdrawClose === s ? "default" : "outline"} onClick={() => setWithdrawClose(s)} data-testid={`withdraw-status-${s || "none"}`}>
                    {s || "nu schimba"}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialog(null)}>Anulează</Button>
            <Button onClick={handleWithdraw} data-testid="confirm-withdraw">Marchează retras</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline create agent dialog */}
      <Dialog open={!!createDialog} onOpenChange={() => setCreateDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Creează Agent nou</DialogTitle>
            <DialogDescription>Va fi atribuit imediat acestui grup și aliasul va fi salvat permanent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nume complet *</Label>
              <Input value={newAgentForm.full_name} onChange={(e) => setNewAgentForm({ ...newAgentForm, full_name: e.target.value })} data-testid="new-agent-full-name" />
            </div>
            <div>
              <Label>Cod Marca primar (opțional)</Label>
              <Input value={newAgentForm.primary_marca_code} onChange={(e) => setNewAgentForm({ ...newAgentForm, primary_marca_code: e.target.value })} data-testid="new-agent-marca" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(null)}>Anulează</Button>
            <Button onClick={handleCreateAgent} data-testid="confirm-create-agent">Creează și mapează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reconciliation;
